import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  height: 100vh;
  overflow: hidden;

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 0 8px 0;
    margin-bottom: 1.5em;

    .header-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${({ theme }) => theme.text};
      font-size: 20px;
      margin-right: 1em;
      opacity: 0.5;
    }

    .header-details {
      flex: 1;

      h3 {
        margin: 0 0 6px 0;
        font-size: 1.1em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.02em;
        opacity: 0.9;
      }

      .header-meta {
        display: flex;
        gap: 8px;
        font-size: 0.75em;

        span {
          padding: 2px 6px;
          border-radius: 6px;
          font-weight: 400;
          opacity: 0.7;
        }

        .update-tag {
          background: rgba(139, 92, 246, 0.08);
          color: ${({ theme }) => theme.text};
        }
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 24px;

    &.main-stats {
      margin-bottom: 20px;
    }

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }

  .kpi-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.03);
      transform: translateY(-0.5px);
    }

    &.primary {
      border-left: 4px solid ${({ theme }) => theme.primary};
      .thumb {
        color: ${({ theme }) => theme.primary};
      }
    }

    &.success {
      border-left: 4px solid #10b981;
      .thumb {
        color: #10b981;
      }
    }

    &.info {
      border-left: 4px solid #3b82f6;
      .thumb {
        color: #3b82f6;
      }
    }

    &.warning {
      border-left: 4px solid #f59e0b;
      .thumb {
        color: #f59e0b;
      }
    }

    .card-content {
      display: flex;
      align-items: center;
      gap: 16px;

      .thumb {
        opacity: 0.7;
        font-size: 1.1em;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 4px;

        p {
          margin: 0;
        }

        p:first-child {
          font-size: 20px;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 13px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          font-weight: 500;
        }
      }
    }
  }

  /* ========== SPLIT LAYOUT ========== */
  .split-layout {
    display: grid;
    grid-template-columns: 40% 60%;
    gap: 24px;
    margin-top: 16px;
    overflow: hidden;
    height: calc(100vh - 250px);

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
      height: auto;
    }
  }

  /* ========== CONFIGURATION SECTION ========== */
  .config-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    padding-right: 0.5em;

    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.background};
      border-radius: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.primary}40;
      border-radius: 6px;
      
      &:hover {
        background: ${({ theme }) => theme.primary}60;
      }
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      h4 {
        margin: 0;
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.9;
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          font-size: 1.1em;
          opacity: 0.7;
        }
      }
    }

    .config-panel {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 24px;

      .config-group {
        display: flex;
        flex-direction: column;
        gap: 12px;

        label {
          font-size: 0.85em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
          display: flex;
          align-items: center;
          gap: 6px;

          svg {
            font-size: 1.1em;
          }

          .count {
            margin-left: auto;
            font-weight: 400;
            opacity: 0.6;
            font-size: 0.95em;
          }
        }

        .date-inputs {
          display: flex;
          align-items: center;
          gap: 12px;

          .date-field {
            flex: 1;
            position: relative;
            display: flex;
            align-items: center;

            svg {
              position: absolute;
              left: 12px;
              font-size: 0.9em;
              color: ${({ theme }) => theme.text};
              opacity: 0.4;
              pointer-events: none;
            }

            input {
              width: 100%;
              padding: 10px 12px 10px 36px;
              background: ${({ theme }) => theme.card};
              border: 1px solid ${({ theme }) => theme.border};
              border-radius: 12px;
              color: ${({ theme }) => theme.text};
              font-size: 0.9em;
              font-family: 'Inter', system-ui, sans-serif;
              transition: all 0.2s ease;

              &:focus {
                outline: none;
                border-color: ${({ theme }) => theme.primary};
                box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
              }

              &.error {
                border-color: #ef4444;
              }

              &::placeholder {
                color: ${({ theme }) => theme.text};
                opacity: 0.4;
              }
            }
          }

          .separator {
            font-size: 0.85em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .date-error {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          color: #ef4444;
          font-size: 0.8em;

          svg {
            font-size: 1.1em;
            flex-shrink: 0;
          }
        }

        .company-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
          padding: 4px;

          &::-webkit-scrollbar {
            width: 6px;
          }
          
          &::-webkit-scrollbar-track {
            background: ${({ theme }) => theme.background};
            border-radius: 3px;
          }
          
          &::-webkit-scrollbar-thumb {
            background: ${({ theme }) => theme.primary}40;
            border-radius: 3px;
            
            &:hover {
              background: ${({ theme }) => theme.primary}60;
            }
          }

          .company-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            background: ${({ theme }) => theme.contrast};
            border: 1px solid ${({ theme }) => theme.border};
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background: ${({ theme }) => theme.contrast2};
              transform: translateX(2px);
            }

            &.selected {
              background: rgba(16, 185, 129, 0.1);
              border-color: rgba(16, 185, 129, 0.3);

              .company-cube {
                background: rgba(16, 185, 129, 0.15);
              }
            }

            .company-cube {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 36px;
              height: 36px;
              background: ${({ theme }) => theme.card};
              border-radius: 12px;
              font-size: 1.3em;
              flex-shrink: 0;
            }

            .company-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 2px;

              .company-name {
                font-size: 0.85em;
                font-weight: 500;
                color: ${({ theme }) => theme.text};
              }

              .company-cnpj {
                font-size: 0.75em;
                color: ${({ theme }) => theme.text};
                opacity: 0.6;
              }
            }
          }
        }
      }

      .start-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 14px 20px;
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        border-radius: 12px;
        color: #10b981;
        font-size: 0.9em;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Inter', system-ui, sans-serif;

        svg {
          font-size: 1.2em;

          &.spin {
            animation: ${spin} 1s linear infinite;
          }
        }

        &:hover:not(:disabled) {
          background: rgba(16, 185, 129, 0.2);
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }

  /* ========== FILES SECTION ========== */
  .files-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow: hidden;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      h4 {
        margin: 0;
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.9;
        display: flex;
        align-items: center;
        gap: 8px;

        svg {
          font-size: 1.1em;
          opacity: 0.7;
        }
      }

      .select-all-btn {
        padding: 8px 14px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 6px;
        color: ${({ theme }) => theme.text};
        font-size: 0.8em;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Inter', system-ui, sans-serif;

        &:hover {
          background: ${({ theme }) => theme.contrast};
        }
      }
    }

    /* ========== FILES TOOLBAR ========== */
    .files-toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      margin-bottom: 16px;

      .search-box {
        flex: 1;
        position: relative;
        display: flex;
        align-items: center;

        .search-icon {
          position: absolute;
          left: 12px;
          font-size: 1em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          pointer-events: none;
        }

        input {
          width: 100%;
          padding: 10px 14px 10px 40px;
          background: ${({ theme }) => theme.contrast};
          border: 1px solid ${({ theme }) => theme.border};
          border-radius: 12px;
          color: ${({ theme }) => theme.text};
          font-size: 0.9em;
          font-family: 'Inter', system-ui, sans-serif;
          transition: all 0.2s ease;

          &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.primary};
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
          }

          &::placeholder {
            color: ${({ theme }) => theme.text};
            opacity: 0.4;
          }
        }
      }

      .toolbar-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border-radius: 12px;
        font-size: 0.85em;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: 'Inter', system-ui, sans-serif;
        white-space: nowrap;

        svg {
          font-size: 1.1em;
        }

        &.upload {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          color: #8b5cf6;

          &:hover {
            background: rgba(139, 92, 246, 0.2);
            transform: translateY(-1px);
          }
        }

        &.select-all {
          background: ${({ theme }) => theme.contrast};
          border: 1px solid ${({ theme }) => theme.border};
          color: ${({ theme }) => theme.text};

          &:hover {
            background: ${({ theme }) => theme.contrast2};
          }
        }
      }

      @media (max-width: 900px) {
        flex-wrap: wrap;

        .search-box {
          flex: 1 1 100%;
          order: 1;
        }

        .toolbar-btn {
          flex: 1;
          justify-content: center;
          order: 2;
        }
      }
    }

    .files-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow-y: auto;
      padding-right: 0.5em;
      height: 100%;

      &::-webkit-scrollbar {
        width: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.background};
        border-radius: 8px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.primary}40;
        border-radius: 6px;
        
        &:hover {
          background: ${({ theme }) => theme.primary}60;
        }
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;

        svg {
          font-size: 3em;
          margin-bottom: 16px;

          &.spin {
            animation: ${spin} 1s linear infinite;
          }
        }

        p {
          font-size: 0.95em;
          margin: 0 0 4px 0;
        }

        span {
          font-size: 0.8em;
          opacity: 0.7;
        }
      }

      .file-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        padding: 16px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transform: translateY(-1px);
        }

        &.selected {
          background: rgba(16, 185, 129, 0.05);
          border-color: rgba(16, 185, 129, 0.3);
        }

        .file-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;

          .file-checkbox {
            display: flex;
            align-items: center;

            input[type="checkbox"] {
              width: 18px;
              height: 18px;
              cursor: pointer;
              accent-color: ${({ theme }) => theme.primary};
            }
          }

          .file-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: rgba(16, 185, 129, 0.1);
            border-radius: 12px;
            color: #10b981;
            font-size: 1.3em;
            flex-shrink: 0;
          }

          .file-title {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 10px;

            .filename {
              font-size: 0.9em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              font-family: "Inter", monospace;
            }

            .status-badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 6px;
              font-size: 0.7em;
              font-weight: 500;
              text-transform: uppercase;

              &.pending {
                background: rgba(245, 158, 11, 0.1);
                color: #f59e0b;
              }

              &.processed {
                background: rgba(16, 185, 129, 0.1);
                color: #10b981;
              }
            }
          }
        }

        .file-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          padding-left: 42px;

          .company {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.85em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};

            svg {
              font-size: 1.1em;
              opacity: 0.6;
            }
          }

          .cnpj {
            font-size: 0.75em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }
        }

        .file-info {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
          padding-left: 42px;
          flex-wrap: wrap;

          .info-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.7;

            svg {
              font-size: 1em;
              opacity: 0.6;
            }
          }
        }

        .file-footer {
          padding-left: 42px;
          padding-top: 8px;
          border-top: 1px solid ${({ theme }) => theme.border};

          .upload-date {
            font-size: 0.75em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }
      }
    }
  }
`;
